import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControlLabel,
  Checkbox,
  TextField,
} from '@mui/material';
import { useMutation, gql } from '@apollo/client';
import { useSnackbar } from '../../providers/SnackbarProvider';

const BULK_ESTIMATE_GDRIVE_CONFIG = gql`
  mutation bulkEstimateGdriveConfig($companyIds: [Int], $limit: Int, $autoConfigureHighConfidence: Boolean) {
    bulkEstimateGdriveConfig(
      companyIds: $companyIds
      limit: $limit
      autoConfigureHighConfidence: $autoConfigureHighConfidence
    ) {
      success
      message
      totalProcessed
      autoConfiguredCount
      requiresConfirmationCount
      results {
        companyId
        companyName
        estimate {
          sharedDriveName
          directoryName
          directoryId
          confidence
          alternatives
          requiresUserConfirmation
        }
        error
      }
    }
  }
`;

interface EstimateResult {
  companyId: number;
  companyName?: string;
  estimate?: {
    sharedDriveName: string;
    directoryName: string;
    directoryId?: string | null;
    confidence: string;
    alternatives: string[];
    requiresUserConfirmation: boolean;
  };
  error?: string;
}

const GDriveBulkConfigSection: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [limit, setLimit] = useState<number>(50);
  const [autoConfigureHighConfidence, setAutoConfigureHighConfidence] = useState(false);
  const [results, setResults] = useState<EstimateResult[]>([]);
  const [summary, setSummary] = useState<{
    totalProcessed: number;
    autoConfiguredCount: number;
    requiresConfirmationCount: number;
  } | null>(null);

  const [bulkEstimate, { loading }] = useMutation(BULK_ESTIMATE_GDRIVE_CONFIG, {
    onCompleted: data => {
      const result = data.bulkEstimateGdriveConfig;
      if (result.success) {
        setResults(result.results);
        setSummary({
          totalProcessed: result.totalProcessed,
          autoConfiguredCount: result.autoConfiguredCount,
          requiresConfirmationCount: result.requiresConfirmationCount,
        });
        showSnackbar(result.message, 'success');
      } else {
        showSnackbar(result.message || 'Bulk estimation failed', 'error');
      }
    },
    onError: error => {
      console.error('Bulk estimation error:', error);
      showSnackbar(`Error: ${error.message}`, 'error');
    },
  });

  const handleRunBulkEstimate = () => {
    setResults([]);
    setSummary(null);
    bulkEstimate({
      variables: {
        limit,
        autoConfigureHighConfidence,
      },
    });
  };

  const getConfidenceChip = (confidence: string) => {
    const colorMap: Record<string, 'success' | 'warning' | 'error'> = {
      high: 'success',
      medium: 'warning',
      low: 'error',
    };
    return <Chip label={confidence} color={colorMap[confidence] || 'default'} size="small" />;
  };

  return (
    <Box mt={4} p={3} sx={{ backgroundColor: 'var(--surface-raised)', borderRadius: 1 }}>
      <Typography variant="h5" mb={2}>
        GDrive Bulk Configuration
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Estimate GDrive configuration for companies that haven&apos;t been configured yet. The system will attempt to
        match company names with existing Google Drive directories.
      </Typography>

      <Box display="flex" gap={2} alignItems="center" mb={2}>
        <TextField
          label="Companies to process"
          type="number"
          size="small"
          value={limit}
          onChange={e => setLimit(Math.min(200, Math.max(1, parseInt(e.target.value) || 1)))}
          inputProps={{ min: 1, max: 200 }}
          sx={{ width: 150 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={autoConfigureHighConfidence}
              onChange={e => setAutoConfigureHighConfidence(e.target.checked)}
            />
          }
          label="Auto-configure high confidence matches"
        />

        <Button variant="contained" onClick={handleRunBulkEstimate} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Run Bulk Estimation'}
        </Button>
      </Box>

      {autoConfigureHighConfidence && (
        <Alert severity="info" sx={{ mb: 2 }}>
          High confidence matches (exact directory name found) will be automatically configured without user
          confirmation.
        </Alert>
      )}

      {summary && (
        <Box mb={2} display="flex" gap={2}>
          <Chip label={`Total: ${summary.totalProcessed}`} variant="outlined" />
          <Chip label={`Auto-configured: ${summary.autoConfiguredCount}`} color="success" variant="outlined" />
          <Chip label={`Needs confirmation: ${summary.requiresConfirmationCount}`} color="warning" variant="outlined" />
        </Box>
      )}

      {results.length > 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Company ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Shared Drive</TableCell>
                <TableCell>Directory</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map(result => (
                <TableRow key={result.companyId}>
                  <TableCell>{result.companyId}</TableCell>
                  <TableCell>{result.companyName || '-'}</TableCell>
                  <TableCell>{result.estimate?.sharedDriveName || '-'}</TableCell>
                  <TableCell>{result.estimate?.directoryName || '-'}</TableCell>
                  <TableCell>{result.estimate ? getConfidenceChip(result.estimate.confidence) : '-'}</TableCell>
                  <TableCell>
                    {result.error ? (
                      <Chip label="Error" color="error" size="small" />
                    ) : result.estimate?.directoryId ? (
                      <Chip label="Found" color="success" size="small" />
                    ) : (
                      <Chip label="Not found" color="warning" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default GDriveBulkConfigSection;
